/**
 * Clôture annuelle atomique et déverrouillage de période.
 *
 * Ordre IMPOSÉ : toutes les écritures de fin d'exercice (amortissements, provisions, résultat, réserve légale,
 * distribution…) sont enregistrées AVANT le verrouillage de la période 12, dans une même séquence
 * atomique (`runAtomicWrites`). Si une étape échoue, les écritures déjà passées sont retirées et la
 * période n'est PAS verrouillée. Le garde de période du dépôt n'a donc aucune exception à faire pour la clôture.
 * Le déverrouillage reste réservé à l'administrateur et est toujours journalisé.
 */
import { runAtomicWrites, type AtomicWrite } from '@application/persistence/AtomicBatch';
import {
  ClotureAnnuelleService,
  type ClotureParams,
} from '@domain/exercices/ClotureAnnuelleService';
import { assertSoldesEquilibresAvantCloture } from '@domain/comptabilite/ControleSoldesCloture';
import { calculerSceauPeriode } from '@domain/comptabilite/ScellementPeriode';
import { totauxPiece } from '@domain/comptabilite/EquilibrePiece';
import type { Ecriture } from '@shared/types/Ecriture';
import type { ExerciceFiscal } from '@shared/types/ExerciceFiscal';
import type { AuditAction } from '@shared/types/AuditLog';

/** Dépendances (ports) : branchées sur `ctx` et les magasins de l'entreprise dans l'application. */
export interface DepsCloture {
  listEcritures(): Promise<Ecriture[]>;
  addEcriture(e: Ecriture): Promise<void>;
  deleteEcriture(id: string): Promise<void>;
  loadCloturees(): Promise<string[]>;
  saveCloturees(periodes: readonly string[]): Promise<void>;
  saveSceau(periode: string, sceau: { sceau: string; nb_ecritures: number } | null): Promise<void>;
  audit(input: {
    action: AuditAction;
    entity: string;
    description: string;
    payload?: Record<string, unknown>;
  }): Promise<void>;
  /** Persistance de l'exercice passé en « clôturé » (étape atomique, avant le verrouillage). */
  saveExercice?(exercice: ExerciceFiscal): Promise<void>;
  restoreExercice?(exercice: ExerciceFiscal): Promise<void>;
}

export class ClotureAnnuelleRefuseeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ClotureAnnuelleRefuseeError';
  }
}

export class DeverrouillageRefuseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DeverrouillageRefuseError';
  }
}

export interface ClotureAnnuelleInput {
  exercice: ExerciceFiscal;
  params: ClotureParams;
  /** Écritures de fin d'exercice déjà construites (amortissements, distribution…) : passées avant le calcul du résultat. */
  ecrituresComplementaires?: readonly Ecriture[];
  peutCloturer: boolean;
}

export interface ClotureAnnuelleResultat {
  periode: string;
  ecritures_passees: number;
  resultat_net: number;
  sceau: string;
}

export async function cloturerExerciceAtomique(
  deps: DepsCloture,
  input: ClotureAnnuelleInput,
): Promise<ClotureAnnuelleResultat> {
  if (!input.peutCloturer) {
    throw new ClotureAnnuelleRefuseeError('Clôture réservée aux profils comptable, RH et administrateur.');
  }
  const periode = `${input.exercice.code}-12`;
  const [courantes, cloturees] = await Promise.all([deps.listEcritures(), deps.loadCloturees()]);
  if (cloturees.includes(periode)) {
    throw new ClotureAnnuelleRefuseeError(`La période ${periode} est déjà clôturée.`);
  }
  const complementaires = [...(input.ecrituresComplementaires ?? [])];
  const base = [...courantes, ...complementaires];
  const r = ClotureAnnuelleService.cloturer(input.exercice, base, input.params);
  const aPasser = [...complementaires, ...r.ecritures_provisions, ...r.ecritures_resultat];
  for (const e of aPasser) {
    if (cloturees.includes(e.periode)) {
      throw new ClotureAnnuelleRefuseeError(`Écriture de clôture dans la période clôturée ${e.periode} : clôture annulée.`);
    }
  }
  const t = totauxPiece(aPasser);
  if (t.debit !== t.credit) {
    throw new ClotureAnnuelleRefuseeError(`Écritures de clôture déséquilibrées (débit ${t.debit} ≠ crédit ${t.credit}).`);
  }
  const apres = [...courantes, ...aPasser];
  assertSoldesEquilibresAvantCloture(apres, periode);
  const sceau = await calculerSceauPeriode(apres.filter((e) => e.periode === periode));
  const nbEcritures = apres.filter((e) => e.periode === periode).length;

  const etapes: AtomicWrite[] = aPasser.map((e) => ({
    table: 'ecritures',
    id: e.id,
    run: () => deps.addEcriture(e),
    rollback: () => deps.deleteEcriture(e.id),
  }));
  if (deps.saveExercice) {
    const ancien: ExerciceFiscal = input.exercice;
    etapes.push({
      table: 'exercices',
      id: ancien.id,
      run: () => deps.saveExercice!(r.exercice),
      rollback: () => (deps.restoreExercice ?? deps.saveExercice!)(ancien),
    });
  }
  // Dernière étape : le verrouillage. Si elle échoue, tout ce qui précède est retiré.
  etapes.push({
    table: 'periodes_cloturees',
    id: periode,
    run: async () => {
      const annuler = async (): Promise<void> => {
        await deps.saveCloturees(cloturees).catch(() => undefined);
        await deps.saveSceau(periode, null).catch(() => undefined);
      };
      try {
        await deps.saveSceau(periode, { sceau, nb_ecritures: nbEcritures });
        await deps.saveCloturees([...cloturees, periode]);
      } catch (err) {
        // Une étape qui échoue n'est pas « terminée » pour l'orchestrateur : elle se défait elle-même.
        await annuler();
        throw err;
      }
    },
    rollback: async () => {
      await deps.saveCloturees(cloturees);
      await deps.saveSceau(periode, null);
    },
  });
  await runAtomicWrites(etapes);
  await deps.audit({
    action: 'cloture',
    entity: 'ecriture',
    description: `Clôture annuelle ${input.exercice.code} : ${aPasser.length} écriture(s) puis verrouillage de ${periode}`,
    payload: { periode, sceau, nb_ecritures: nbEcritures, ecritures_passees: aPasser.length },
  });
  return { periode, ecritures_passees: aPasser.length, resultat_net: r.resultat_net, sceau };
}

/** Déverrouillage (réouverture) d'une période : administrateur uniquement, toujours journalisé. */
export async function deverrouillerPeriode(
  deps: DepsCloture,
  input: { periode: string; estAdmin: boolean; motif?: string },
): Promise<void> {
  if (!input.estAdmin) {
    throw new DeverrouillageRefuseError('Seul l’administrateur peut déverrouiller une période clôturée.');
  }
  const cloturees = await deps.loadCloturees();
  if (!cloturees.includes(input.periode)) {
    throw new DeverrouillageRefuseError(`La période ${input.periode} n’est pas clôturée.`);
  }
  await deps.saveCloturees(cloturees.filter((p) => p !== input.periode));
  await deps.saveSceau(input.periode, null);
  await deps.audit({
    action: 'deverrouillage',
    entity: 'ecriture',
    description: `Déverrouillage de la période ${input.periode}`,
    payload: input.motif ? { periode: input.periode, motif: input.motif } : { periode: input.periode },
  });
}
